import { createRealtimeCall, registerCallId } from '../../apis/realtime/api';
import { audioLatencyStore } from '../../zustand/audio-latency-store';
import { OPENAI_REALTIME_MODEL, readVoiceCompanionSettings } from '@/constants/voiceCompanion';
import { REALTIME_ICE_SERVERS } from './constants';
import {
  describeMediaStream,
  realtimeDebugLog,
  recordRealtimeEvent,
  registerRealtimeDebugProviders,
  updateRealtimeDebugSnapshot,
} from './debug';
import { logOpenAiEvent, logRealtimeEvent } from './event-log';

// One factory call per connection — no singleton, no module state. Everything a
// connection owns dies with its `dispose()`.

function createRealtimeError(message, cause) {
  const error = new Error(message);
  if (cause) {
    error.cause = cause;
  }
  return error;
}

/**
 * A promise that settles when the connection is genuinely usable — meaning the
 * peer connection reached `connected` AND the backend has acknowledged the
 * call id. Awaiting `connect()` therefore means "the backend can now drive the
 * agent", not merely "a socket opened".
 */
function createConnectionStateTracker() {
  let resolveReady;
  let rejectReady;
  let settled = false;

  const readyPromise = new Promise((resolve, reject) => {
    resolveReady = value => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(value);
    };
    rejectReady = error => {
      if (settled) {
        return;
      }
      settled = true;
      reject(error);
    };
  });

  // A dispose() before anyone awaits would otherwise surface as an unhandled
  // rejection. Attaching a no-op handler marks it handled without stopping the
  // real awaiter from seeing it.
  readyPromise.catch(() => {});

  return { readyPromise, resolveReady: v => resolveReady(v), rejectReady: e => rejectReady(e) };
}

export function createRealtimeWebRtcConnection({
  sessionId,
  exerciseId,
  onRemoteStream,
  onMessage,
  onStateChange,
  onConnected,
  onDisconnected,
}) {
  let peerConnection = null;
  let dataChannel = null;
  let callId = null;
  let disposed = false;
  let registerPromise = null;
  // Diagnostics only — connect() start to the peer connection reaching
  // 'connected' (registerConnectedCall's success is where onConnected fires
  // below), i.e. the full SDP-through-backend + ICE + register handshake.
  let connectStartedAt = null;

  const abortController = new AbortController();
  const tracker = createConnectionStateTracker();

  // Called after every await. A disposed connection must not keep working —
  // otherwise an unmount mid-handshake leaves a live call nobody owns.
  const ensureActive = () => {
    if (disposed) {
      throw createRealtimeError('Realtime connection was disposed mid-connect');
    }
  };

  const handleDataChannelMessage = event => {
    let parsed = null;
    try {
      parsed = JSON.parse(event.data);
    } catch (_error) {
      realtimeDebugLog('data channel sent a non-JSON frame', event.data);
      return;
    }

    // Recorded before anything interprets it — this buffer is the record of what
    // OpenAI actually sent, independent of what we do with it.
    recordRealtimeEvent(parsed);
    logOpenAiEvent(parsed);

    // The data channel is observability only — all agent control is server-side.
    if (parsed.type === 'conversation.item.created' && parsed.item?.role === 'user') {
      audioLatencyStore.queue.push({
        id: parsed.item?.id ?? crypto.randomUUID(),
        startedAt: performance.now(),
      });
    }
    if (parsed.type === 'output_audio_buffer.started') {
      audioLatencyStore.firstAudiblePending = true;
    }
    if (parsed.type === 'output_audio_buffer.stopped') {
      audioLatencyStore.firstAudiblePending = false;
      audioLatencyStore.signalPresent = false;
    }

    onMessage?.(parsed);
  };

  const registerConnectedCall = () => {
    // Guarded so a repeated `connected` state change can't register twice.
    if (!registerPromise) {
      registerPromise = registerCallId(callId, sessionId, exerciseId, {
        signal: abortController.signal,
      });
    }
    return registerPromise;
  };

  const handleConnectionStateChange = () => {
    if (!peerConnection) {
      return;
    }

    const state = peerConnection.connectionState;
    onStateChange?.(state);
    realtimeDebugLog(`connection state ${state}`, {
      ice: peerConnection.iceConnectionState,
      signaling: peerConnection.signalingState,
    });
    logRealtimeEvent({
      source: 'webrtc',
      type: `pc.${state}`,
      summary: `ice=${peerConnection.iceConnectionState}`,
    });
    updateRealtimeDebugSnapshot({
      connectionState: state,
      iceConnectionState: peerConnection.iceConnectionState,
      signalingState: peerConnection.signalingState,
    });

    if (state === 'connected') {
      registerConnectedCall()
        .then(() => {
          logRealtimeEvent({ source: 'api', type: 'register-call.ok', summary: callId });
          const setupDurationMs =
            connectStartedAt !== null ? Math.round(performance.now() - connectStartedAt) : null;
          onConnected?.({ callId, setupDurationMs });
          tracker.resolveReady({ callId });
        })
        .catch(error => {
          // Keep the backend's own `error` text — it names the actual cause
          // (quota, region block, outage) and the wrapper alone does not.
          const wrapped = createRealtimeError(
            error?.message
              ? `Failed to register realtime call id: ${error.message}`
              : 'Failed to register realtime call id',
            error
          );
          logRealtimeEvent({
            source: 'error',
            type: 'register-call.failed',
            summary: error?.message ?? 'unknown',
          });
          onDisconnected?.(wrapped);
          tracker.rejectReady(wrapped);
        });
      return;
    }

    if (state === 'failed') {
      const error = createRealtimeError('Realtime peer connection failed');
      onDisconnected?.(error);
      tracker.rejectReady(error);
      return;
    }

    if (state === 'closed' || state === 'disconnected') {
      const error = createRealtimeError(`Realtime peer connection ${state}`);
      onDisconnected?.(error);
      tracker.rejectReady(error);
    }
  };

  const createPeerConnection = () => {
    const pc = new RTCPeerConnection({ iceServers: REALTIME_ICE_SERVERS });
    // recvonly: we never send audio, so the mic is never opened.
    pc.addTransceiver('audio', { direction: 'recvonly' });
    // The channel name is fixed by the Realtime API.
    dataChannel = pc.createDataChannel('oai-events');
    dataChannel.addEventListener('message', handleDataChannelMessage);
    dataChannel.addEventListener('open', () => {
      realtimeDebugLog('data channel open');
      logRealtimeEvent({ source: 'webrtc', type: 'datachannel.open', summary: 'oai-events' });
    });
    dataChannel.addEventListener('close', () => {
      realtimeDebugLog('data channel closed');
      logRealtimeEvent({ source: 'webrtc', type: 'datachannel.close', summary: 'oai-events' });
    });

    pc.ontrack = event => {
      const stream = event.streams?.[0] || event.stream || null;
      const described = describeMediaStream(stream);
      realtimeDebugLog('remote track', described);
      logRealtimeEvent({
        source: 'webrtc',
        type: 'track',
        summary: `${described?.audioTracks?.length ?? 0} audio track(s)`,
        detail: described,
      });
      onRemoteStream?.(stream);
    };
    pc.onconnectionstatechange = handleConnectionStateChange;

    // ICE-specific signals, ahead of and separate from connectionstatechange:
    // a gathering stuck at 'gathering' (no usable candidate ever found — the
    // STUN server above exists exactly to prevent this) or a candidate error
    // is the earliest sign of the "ICE failed" case, and it doesn't reach
    // connectionstatechange until ICE has already given up.
    pc.onicegatheringstatechange = () => {
      updateRealtimeDebugSnapshot({ iceGatheringState: pc.iceGatheringState });
      logRealtimeEvent({
        source: 'webrtc',
        type: 'ice.gathering',
        summary: pc.iceGatheringState,
      });
    };
    pc.onicecandidateerror = event => {
      realtimeDebugLog('ICE candidate error', {
        errorCode: event.errorCode,
        errorText: event.errorText,
        url: event.url,
      });
      logRealtimeEvent({
        source: 'error',
        type: 'ice.candidateError',
        summary: `${event.errorCode ?? '?'} ${event.errorText ?? ''} (${event.url ?? 'no url'})`,
      });
    };

    // Lets `window.__realtimeAudioReport()` read live RTP stats off this exact
    // peer connection.
    registerRealtimeDebugProviders({ getPeerConnection: () => peerConnection });

    return pc;
  };

  const closePeerConnection = () => {
    try {
      dataChannel?.close();
    } catch (_error) {
      // Already closed.
    }
    dataChannel = null;

    if (!peerConnection) {
      return;
    }

    peerConnection.ontrack = null;
    peerConnection.onconnectionstatechange = null;
    peerConnection.onicegatheringstatechange = null;
    peerConnection.onicecandidateerror = null;

    // Stop every track in both directions or the browser keeps the audio
    // pipeline (and on some platforms the device) marked as in use.
    try {
      peerConnection.getReceivers?.().forEach(receiver => {
        try {
          receiver.track?.stop();
        } catch (_error) {
          /* noop */
        }
      });
    } catch (_error) {
      /* noop */
    }

    try {
      peerConnection.getSenders?.().forEach(sender => {
        try {
          sender.track?.stop();
        } catch (_error) {
          /* noop */
        }
      });
    } catch (_error) {
      /* noop */
    }

    try {
      peerConnection.close();
    } catch (_error) {
      /* noop */
    }

    peerConnection = null;
  };

  return {
    async connect() {
      ensureActive();
      connectStartedAt = performance.now();
      peerConnection = createPeerConnection();

      // Read per attempt rather than captured once: the session's pick lives in
      // localStorage until the user returns to /home, so a reconnect later in the
      // session still asks for the same model. Nothing stored (no prescription
      // pick reached localStorage) falls back to the full realtime model rather
      // than omitting `model` and leaving it to the backend's own default.
      const { openai_realtime_model: openaiRealtimeModel } = readVoiceCompanionSettings();

      const offer = await peerConnection.createOffer();
      ensureActive();

      await peerConnection.setLocalDescription(offer);
      ensureActive();
      logRealtimeEvent({
        source: 'api',
        type: 'sdp.offer',
        summary: `posting to the backend (session ${sessionId})`,
      });

      // Through our backend, which signs the call with the server API key — the
      // only kind of call the sideband can attach to. See apis/realtime/api.js.
      const response = await createRealtimeCall(peerConnection.localDescription.sdp, sessionId, {
        signal: abortController.signal,
        openaiRealtimeModel: openaiRealtimeModel ?? OPENAI_REALTIME_MODEL.full,
      });
      ensureActive();

      callId = response.callId;
      updateRealtimeDebugSnapshot({ callId });
      logRealtimeEvent({ source: 'api', type: 'sdp.answer', summary: `callId ${callId}` });

      await peerConnection.setRemoteDescription(
        new RTCSessionDescription({ type: 'answer', sdp: response.answerSdp })
      );
      ensureActive();

      // Resolves only once connected *and* registered — see the tracker.
      return tracker.readyPromise;
    },

    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;

      try {
        abortController.abort();
      } catch (_error) {
        /* noop */
      }

      tracker.rejectReady(createRealtimeError('Realtime connection disposed'));
      closePeerConnection();
    },

    getCallId: () => callId,
    getPeerConnection: () => peerConnection,
  };
}
